import { StoryGroup } from "@/@types/stories.types";
import { StoryProgress } from "./StoryProgress";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

function preloadImage(src: string) {
  const img = new Image();
  img.src = src;
}

interface StoryViewerProps {
  storyGroups: StoryGroup[];
  initialGroupIndex: number;
  onClose: () => void;
  onStoryViewed: (groupId: string) => void;
}

export function StoryViewer({
  storyGroups,
  initialGroupIndex,
  onClose,
  onStoryViewed,
}: StoryViewerProps) {
  const [groupIndex, setGroupIndex] = useState(initialGroupIndex);
  const [storyIndex, setStoryIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const currentGroup = storyGroups[groupIndex];
  const currentStory = currentGroup?.stories[storyIndex];
  const duration = currentStory?.duration || 5000;

  // Preload all images for current group and next group on mount
  useEffect(() => {
    const imagesToPreload: string[] = [];

    // All images in current group
    currentGroup?.stories.forEach((story) => {
      if (story.imageUrl) imagesToPreload.push(story.imageUrl);
    });

    // All images in next group
    const nextGroup = storyGroups[groupIndex + 1];
    nextGroup?.stories.forEach((story) => {
      if (story.imageUrl) imagesToPreload.push(story.imageUrl);
    });

    imagesToPreload.forEach(preloadImage);
  }, [groupIndex, currentGroup, storyGroups]);

  // Reset image loaded state when story changes
  useEffect(() => {
    setIsImageLoaded(false);
  }, [groupIndex, storyIndex]);

  const goToNextStory = useCallback(() => {
    if (storyIndex < currentGroup.stories.length - 1) {
      setStoryIndex((prev) => prev + 1);
    } else if (groupIndex < storyGroups.length - 1) {
      onStoryViewed(currentGroup.id);
      setGroupIndex((prev) => prev + 1);
      setStoryIndex(0);
    } else {
      onStoryViewed(currentGroup.id);
      onClose();
    }
  }, [storyIndex, groupIndex, currentGroup, storyGroups.length, onClose, onStoryViewed]);

  const goToPrevStory = useCallback(() => {
    if (storyIndex > 0) {
      setStoryIndex((prev) => prev - 1);
    } else if (groupIndex > 0) {
      setGroupIndex((prev) => prev - 1);
      setStoryIndex(storyGroups[groupIndex - 1].stories.length - 1);
    }
  }, [storyIndex, groupIndex, storyGroups]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") goToNextStory();
      if (e.key === "ArrowLeft") goToPrevStory();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, goToNextStory, goToPrevStory]);

  if (!currentStory) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95">
      <div className="relative h-full w-full max-w-md">
        <StoryProgress
          total={currentGroup.stories.length}
          current={storyIndex}
          duration={duration}
          isPaused={isPaused || !isImageLoaded}
          onComplete={goToNextStory}
        />

        <button
          onClick={onClose}
          className="absolute right-4 top-8 z-10 rounded-full p-2 text-white transition-colors hover:bg-white/10"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="absolute left-4 top-12 z-10 flex items-center gap-3">
          <img
            src={currentGroup.avatarUrl}
            alt={currentGroup.name}
            className="h-10 w-10 rounded-full object-cover ring-2 ring-white"
          />
          <span className="font-medium text-white">{currentGroup.name}</span>
        </div>

        <div
          className="absolute inset-0 flex"
          onMouseDown={() => setIsPaused(true)}
          onMouseUp={() => setIsPaused(false)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          <button onClick={goToPrevStory} className="group h-full w-1/3 focus:outline-none">
            <ChevronLeft className="absolute left-4 top-1/2 h-8 w-8 -translate-y-1/2 text-white opacity-0 transition-opacity group-hover:opacity-50" />
          </button>
          <div className="h-full w-1/3" />
          <button onClick={goToNextStory} className="group h-full w-1/3 focus:outline-none">
            <ChevronRight className="absolute right-4 top-1/2 h-8 w-8 -translate-y-1/2 text-white opacity-0 transition-opacity group-hover:opacity-50" />
          </button>
        </div>

        {!isImageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        )}
        <img
          src={currentStory.imageUrl}
          alt={currentStory.title || "Story"}
          className={`h-full w-full object-cover transition-opacity duration-200 ${isImageLoaded ? "opacity-100" : "opacity-0"}`}
          onLoad={() => setIsImageLoaded(true)}
        />

        {(currentStory.title || currentStory.description || currentStory.link) && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 pt-20">
            {currentStory.title && (
              <h3 className="mb-2 text-xl font-bold text-white">{currentStory.title}</h3>
            )}
            {currentStory.description && (
              <p className="mb-4 text-sm text-white/90">{currentStory.description}</p>
            )}
            {currentStory.link &&
              (currentStory.link.startsWith("http") ? (
                <a href={currentStory.link} target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="w-full border-white/30 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
                  >
                    {currentStory.linkText || "Подробнее"}
                  </Button>
                </a>
              ) : (
                <Link to={currentStory.link} onClick={onClose}>
                  <Button
                    variant="outline"
                    className="w-full border-white/30 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
                  >
                    {currentStory.linkText || "Подробнее"}
                  </Button>
                </Link>
              ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
